import { AfterViewInit, Component, inject, Input, OnDestroy } from '@angular/core';
import * as echarts from 'echarts';
import { applyRoleVisibilityToOption } from '../../utils/chart-role-visibility.utils';
import { Subscription } from 'rxjs';
import { ConfigService } from '../../shared-services/config.service';
import { ChartWrapperService, ChartWrapperInstance } from '../../shared-services/chart-wrapper.service';
import { ResponsiveLayoutService } from '../../shared-services/responsive-layout.service';
import { ThemeService } from '../../shared-services/theme.service';
import { DataErrorComponent } from '../../global-components/data-error/data-error.component';
import { buildConfigurableTooltipFormatter, buildConfigurableAxisLabel } from '../../utils/chart.utils';
import { CommonModule } from '@angular/common';
import { buildSecondaryXAxisOption, buildSecondaryYAxisOption, getDoubleAxisGridAdjustments } from '../../utils/axis.utils';

import { CapitalCasePipe } from '../../pipes/capital-case.pipe';

@Component({
  selector: 'app-waterfall-chart',
  imports: [DataErrorComponent, CommonModule, CapitalCasePipe],
  templateUrl: './waterfall-chart.component.html',
  styleUrl: './waterfall-chart.component.scss',
})
export class WaterfallChartComponent implements AfterViewInit, OnDestroy {
  @Input() data!: any;
  protected myChart!: echarts.ECharts;
  public hasError = false;
  public errorMessage = '';
  protected allCategories: string[] = [];

  protected wrapper: ChartWrapperInstance = inject(ChartWrapperService).createInstance();
  private configService = inject(ConfigService);
  private responsiveLayoutService = inject(ResponsiveLayoutService);
  private themeService = inject(ThemeService);
  private reflowSub?: Subscription;
  private themeSub?: Subscription;
  private lastChartData?: { category: string[]; series: any[] };

  ngAfterViewInit(): void {
    const initialFilters = this.wrapper.attach(this.data, {
      onReload: (filters) => this.loadAndRenderChart(filters),
      getChart: () => this.myChart,
      getSeriesNames: () => [],
      getCategories: () => this.allCategories,
      // Custom highlight: waterfall uses 0.3 opacity (not 0)
      onHighlightCategories: (cats) => this.applyCategoryHighlight(cats),
      onResetCategoryHighlight: () => this.resetCategoryHighlight(),
    });

    this.loadAndRenderChart(initialFilters ?? undefined);

    this.reflowSub = this.responsiveLayoutService.reflow$.subscribe(() => {
      this.myChart?.resize();
    });

    // Re-render with fresh theme tokens when the user switches theme at runtime.
    // Dispose + re-init so ECharts picks up the updated 'dvm' registered theme.
    this.themeSub = this.themeService.theme$.subscribe(() => {
      if (!this.lastChartData || !this.myChart) return;
      const chartDom = document.getElementById(this.data.widgetId);
      if (!chartDom) return;
      this.myChart.dispose();
      this.myChart = echarts.init(chartDom, 'dvm');
      this.renderChart(this.lastChartData);
    });
  }

  protected loadAndRenderChart(filters?: any): void {
    const chartDom = document.getElementById(this.data.widgetId);
    if (!chartDom) return;

    if (!this.myChart) {
      this.myChart = echarts.init(chartDom, 'dvm');
    }

    this.myChart.showLoading();

    this.configService.getConfig(this.data.apiUrl, this.data, filters)?.subscribe({
      next: (res: any) => {
        const chartData = this.extractChartData(res);
        const isEmpty = !chartData || (Array.isArray(chartData) && chartData.length === 0);

        if (isEmpty) {
          this.hasError = true;
          this.errorMessage = 'No data found for the selected filters.';
          this.myChart?.clear();
        } else {
          this.hasError = false;
          this.renderChart(chartData);
          this.addResizeListener();
        }

        this.myChart?.hideLoading();
      },
      error: () => {
        this.hasError = true;
        this.errorMessage = 'No data available for the chart.';
        this.myChart?.clear();
        this.myChart?.hideLoading();
      },
    });

  }

  protected extractChartData(res: any): { category: string[]; series: any[] } | undefined {
    const dataKey = this.data?.dataKey;

    // New platform format: res[dataKey] has { category, series }
    if (dataKey && res?.[dataKey]?.category && res?.[dataKey]?.series) {
      return res[dataKey];
    }

    // Old format: res.data has { category, series }
    if (res?.data?.category && res?.data?.series) {
      return res.data;
    }

    // Direct format: res itself has { category, series }
    if (res?.category && res?.series) {
      return res;
    }

    return undefined;
  }

  protected renderChart(chartData?: { category: string[]; series: any[] }): void {
    if (chartData) this.lastChartData = chartData;
    const theme = this.themeService.resolve(this.data?.themeOverrides);
    this.allCategories = chartData?.category || [];

    const categories = this.allCategories;
    const gridAdj = getDoubleAxisGridAdjustments(this.data);

    const primaryXAxis: any = { type: 'category', triggerEvent: true };
    const secondaryX = buildSecondaryXAxisOption(this.data, categories);
    const xAxisOption = secondaryX ? [primaryXAxis, secondaryX.axis] : primaryXAxis;

    const primaryYAxis: any = {
      type: 'value',
      ...(buildConfigurableAxisLabel(this.data?.chartConfig?.yAxis)
        ? { axisLabel: buildConfigurableAxisLabel(this.data?.chartConfig?.yAxis) }
        : {}),
    };
    const secondaryY = buildSecondaryYAxisOption(this.data);
    const yAxisOption = secondaryY ? [primaryYAxis, secondaryY] : primaryYAxis;

    const option = {
      title: {},
      tooltip: {
        trigger: 'axis',
        axisPointer: { type: 'shadow' },
        backgroundColor: theme.tooltip?.bg || '#fff',
        borderColor: theme.tooltip?.borderColor || '#ccc',
        textStyle: {
          color: theme.tooltip?.textColor || '#333',
          fontSize: theme.tooltip?.fontSize || 13,
        },
        formatter: (() => {
          const tooltipCfg = this.data?.chartConfig?.tooltip;
          const configurableFormatter = buildConfigurableTooltipFormatter(tooltipCfg);
          if (configurableFormatter) return configurableFormatter;

          return (params: any) => {
            let tar;
            if (params[1] && params[1].value !== '-') {
              tar = params[1];
            } else {
              tar = params[2];
            }
            return `${tar.name}<br/>${tar.seriesName} : ${tar.value}`;
          };
        })(),
      },
      legend: {
        data: ['Expenses', 'Income'],
      },
      grid: {
        left: '3%',
        right: `${4 + gridAdj.extraRight}%`,
        bottom: `${3 + gridAdj.extraBottom}%`,
        containLabel: true,
      },
      xAxis: xAxisOption,
      yAxis: yAxisOption,
      series: (chartData?.series || []).map((s: any) => ({
        ...s,
        label: {
          ...(s?.label || {}),
          color: theme.chart?.textColor || '#333',
          textBorderColor: 'transparent',
          textBorderWidth: 0,
          textShadowBlur: 0,
          textShadowColor: 'transparent',
        },
      })),
    };

    applyRoleVisibilityToOption(option, this.data);

    this.myChart.setOption(option, true);
    setTimeout(() => this.myChart.hideLoading(), 1000);

    // Wrapper re-applies visual state and wires click events
    this.wrapper.afterRender();
  }

  // Custom highlight: waterfall uses opacity 0.3 for dimmed (not 0 like default wrapper)
  protected applyCategoryHighlight(categories?: string[]): void {
    if (!this.myChart) return;
    const option = this.myChart.getOption() as any;
    if (!option?.series) return;

    const cats = categories || this.wrapper.selectedCategories;
    const categoryIndices = cats
      .map(cat => this.allCategories.indexOf(cat))
      .filter(i => i !== -1);
    if (categoryIndices.length === 0) return;

    const updatedSeries = option.series.map((s: any) => {
      if (!Array.isArray(s.data)) return s;
      return {
        ...s,
        data: s.data.map((item: any, idx: number) => {
          const isMatch = categoryIndices.includes(idx);
          if (typeof item === 'object' && item !== null) {
            return { ...item, itemStyle: { ...(item.itemStyle || {}), opacity: isMatch ? 1 : 0.3 } };
          }
          return { value: item, itemStyle: { opacity: isMatch ? 1 : 0.3 } };
        }),
      };
    });

    this.myChart.setOption({ series: updatedSeries });
  }

  protected resetCategoryHighlight(): void {
    if (!this.myChart) return;
    const option = this.myChart.getOption() as any;
    if (!option?.series) return;

    const updatedSeries = option.series.map((s: any) => {
      if (!Array.isArray(s.data)) return s;
      return {
        ...s,
        data: s.data.map((item: any) => {
          if (typeof item === 'object' && item !== null) {
            return { ...item, itemStyle: { ...(item.itemStyle || {}), opacity: 1 } };
          }
          return { value: item, itemStyle: { opacity: 1 } };
        }),
      };
    });

    this.myChart.setOption({ series: updatedSeries });
  }

  protected addResizeListener(): void {
    window.addEventListener('resize', this.handleResize);
  }

  protected handleResize = (): void => {
    this.myChart?.resize();
  };

  ngOnDestroy(): void {
    this.wrapper.detach();
    this.reflowSub?.unsubscribe();
    this.themeSub?.unsubscribe();
    window.removeEventListener('resize', this.handleResize);
    this.myChart?.dispose();
  }
}
